
import * as PIXI from "pixi.js";
import { Assets } from "pixi.js";
import { canvasProperties } from "../config";
import { MixBlend, Spine } from "@esotericsoftware/spine-pixi-v8";
import { brightnessFilter, brightnessFilter2, dullFilter, dullFilter2, font_main, HandleOrientation } from "./global";
import { gsap } from "gsap/gsap-core";
import { LogoAnim, PortraitAdjustFun, landScapeAdjustFun, clearFigmaBaseScaleCache } from "../app";
import { generateBonusRow, currencySymbol } from "./server";
import { SoundManager } from "./sounds";
let visibleElements: PIXI.ContainerChild[] = []
// ✅ Manual tuning for where the $ text sits on the block (positive Y = down)
const CURRENCY_TEXT_OFFSET = new PIXI.Point(0, 0);

type contType = PIXI.Container & {
    animName?: string;
    boxBg?: Spine;
    boxType?: string;
    shatterAnim?: Spine;
    box_Text?: PIXI.Text;
    row_i?: number;
    backUp_Y?: number;
    laser_burst?: Spine;
    win?: number
}
export class Bonus extends PIXI.Container {
    // boxData!:(string|number)[][];
    bonusWin!: number;
    bonusBg!: PIXI.Sprite;
    transition!: Spine;
    roboAnim!: Spine;
    blocksContainer!: PIXI.Container;
    blockAndFrameContainer!: PIXI.Container;
    BlocksColContArr: contType[] = [];
    Ind_BlocksArr: contType[][] = [];
    blocksOrder = [7, 5, 3, 2, 1];
    boxTypes = ["mud", "wood", "brick", "rock", "gold"]
    intialBoxYpos: number = 0
    app!: PIXI.Application;
    beam_anim!: Spine;
    bonusContainer!: PIXI.Container;
    private _beamConstraint?: any;          // PathConstraint named "beam"
    private _boneMain?: any;                // "main"
    private _boneStart?: any;               // "starting point"
    private _boneTarget?: any;              // "target"     
    private bonusInfotext!: PIXI.Text;
    BlocksBg!: PIXI.Sprite;
    rowData!: (string | number)[];
    // Parrot_02 only provides 01..04 variants (no 05/06). Clamp any tier/row to that range.
    private readonly _parrotMaxVariant = 4;

    private parrotVariantForRow(rowIndex: number) {
        // rowIndex is 0-based; parrot animation variants are 1-based.
        const oneBased = rowIndex + 1;
        return Math.max(1, Math.min(this._parrotMaxVariant, oneBased));
    }

    private fmt02(n: number) {
        return String(n).padStart(2, "0");
    }


    constructor() {
        super();
        this.createBonusPage();
        this.visible = false;
    }
    createBonusPage() {
        this.bonusContainer = new PIXI.Container();
        this.bonusContainer.visible = false;
        this.addChild(this.bonusContainer);
        this.createBlocksContainer();
        this.createInfoText()
        this.createRoboAnim();
        this.createTransition();
        HandleOrientation(this.PortraitAdjustFun.bind(this), this.landScapeAdjustFun.bind(this))

    }
    createTransition() {
        this.transition = Spine.from({ skeleton: "bonus_transition_json", atlas: "bonus_transition_atlas" })
        this.transition.state.setAnimation(0, "animation", false);
        this.transition.state.timeScale = 0;
        this.transition.x = canvasProperties.width / 2;
        this.transition.y = canvasProperties.height / 2;
        // this.transition.scale.set(2);
        this.addChild(this.transition);
        this.transition.state.addListener({
            complete: () => {
                if (this.transition.state.getCurrent(0) && this.transition.state.getCurrent(0)?.animation?.name == "animation") {
                    this.showBonus()
                } else {
                    this.transition.visible = false;
                    if (!LogoAnim.allsuper) {
                        this.visible = false;
                    }
                };
            }
        });
    }

    private layoutBonusTransition(isPortrait: boolean) {
        if (!this.transition) return;

        // Source of truth: renderer screen size
        const app = this.app ?? (globalThis as any).__PIXI_APP__;
        const screenW = app?.renderer?.screen?.width ?? canvasProperties.width;
        const screenH = app?.renderer?.screen?.height ?? canvasProperties.height;

        // Ensure transforms don’t compound across calls
        this.transition.scale.set(1);
        this.transition.skew.set(0, 0);

        // Pivot to visual center (use LOCAL bounds)
        // Important: bounds depend on current pose; make sure the spine pose is evaluated
        this.transition.update(0);

        const b = this.transition.getLocalBounds();
        if (b.width < 1 || b.height < 1) {
            // If bounds aren't ready yet (first frame), retry next frame
            requestAnimationFrame(() => this.layoutBonusTransition(isPortrait));
            return;
        }

        this.transition.pivot.set(b.x + b.width / 2, b.y + b.height / 2);

        // Center on screen
        this.transition.position.set(screenW / 2, screenH / 2);

        // Rotate 180° in portrait
        this.transition.rotation = isPortrait ? Math.PI : 0;

        // After rotation/pivot, use WORLD bounds to compute cover scale reliably
        // (getBounds respects rotation/pivot)
        const wb = this.transition.getBounds();
        const bw = Math.max(1, wb.width);
        const bh = Math.max(1, wb.height);

        const sx = screenW / bw;
        const sy = screenH / bh;
        const s = Math.max(sx, sy); // COVER (fills entire screen)

        this.transition.scale.set(s);

        // Re-center after scaling (bounds can shift slightly)
        this.transition.position.set(screenW / 2, screenH / 2);
    }


    private setSpineAnimSafe(sp: Spine, track: number, desired: string, loop: boolean) {
        const anims = sp.skeleton?.data?.animations ?? [];
        const names = anims.map((a: any) => a.name);

        const chosen = names.includes(desired) ? desired : names[0];

        if (!chosen) {
            console.error("[Bonus] No animations found on spine instance.", sp);
            return;
        }

        if (chosen !== desired) {
            console.warn(`[Bonus] Animation '${desired}' not found. Using '${chosen}'. Available:`, names);
        }

        sp.state.setAnimation(track, chosen, loop);
    }

    createRoboAnim() {
        // -----------------------
        // Beam anim (unchanged, but made safe)
        // -----------------------
        this.beam_anim = Spine.from({ skeleton: "beam_anim_anim_json", atlas: "beam_anim_anim_atlas" });
        this.beam_anim.x = canvasProperties.width / 2;
        this.beam_anim.y = canvasProperties.height / 2;
        this.bonusContainer.addChild(this.beam_anim);
        this.beam_anim.visible = false;
        this.setSpineAnimSafe(this.beam_anim, 0, "animation1", false);
        this.beam_anim.state.timeScale = 0;

        // -----------------------
        // Parrot anim (SAFE load)
        // -----------------------
        this.roboAnim = Spine.from({ skeleton: "robo_anim_json", atlas: "robo_anim_atlas", scale: 0.9 });
        this.roboAnim.x = canvasProperties.width / 2;
        this.roboAnim.y = canvasProperties.height / 2;
        this.bonusContainer.addChild(this.roboAnim);

        this.roboAnim.visible = false;
        this.roboAnim.state.setAnimation(0, "idle_01", true);

        this.initBeamRig();
    }

    createInfoText() {
        this.bonusInfotext = new PIXI.Text({
            text: "Tap a block to fire. Reveal cash—or a power up to the next tier.",
            style: {
                fill: 0xffffff,
                fontSize: 60,
                fontWeight: "400",
                fontFamily: "RoadRage",
                align: "center",
                dropShadow: {
                    alpha: 1,
                    angle: 1,
                    blur: 2,
                    color: "0x111111",
                    distance: 2,
                },
                stroke: {
                    color: "black",
                    width: 5,
                    join: "round",
                }
            }
        })
        this.bonusInfotext.anchor.set(0.5);
        this.bonusInfotext.position.set(canvasProperties.width / 2, canvasProperties.height / 2 + 200);
        this.bonusContainer.addChild(this.bonusInfotext);
    }


    private initBeamRig() {
        const sk = this.beam_anim.skeleton;
        this._beamConstraint = sk.findPathConstraint("beam");
        this._boneMain = sk.findBone("main");
        this._boneStart = sk.findBone("starting point");
        this._boneTarget = sk.findBone("target");

        if (!this._beamConstraint || !this._boneMain || !this._boneStart || !this._boneTarget) {
            console.warn("Beam rig: missing path constraint 'beam' or bones 'main'/'starting point'/'target'.");
        }
    }
    private setBoneToGlobal(bone: any, global: PIXI.Point) {
        // 1) Convert global → beam skeleton local
        const pInBeam = this.beam_anim.toLocal(global);

        // 2) Convert to the local space of the bone's *parent*
        const parent = bone.parent; // spine.Bone
        const local = parent.worldToLocal(pInBeam); // <- returns {x,y}

        // 3) Set the bone's local coords
        bone.x = local.x;
        bone.y = local.y;
    }

    private fireBeamTo(box: contType) {
        // 1) World points
        const muzzleGlobal = (() => {
            const muzzleBone = this.roboAnim.skeleton.findBone?.("rhctrl");
            if (muzzleBone) {
                const p = new PIXI.Point(muzzleBone.worldX + 10, muzzleBone.worldY - 10);
                return this.roboAnim.toGlobal(p);
            }
            const r = this.roboAnim.getBounds();
            return new PIXI.Point(r.left, r.y + r.height * 0.5);
        })();
        const targetGlobal = box.getGlobalPosition();

        // 2) Make beam visible and restart any flash/FX animation you’re using
        this.beam_anim.visible = true;
        this.beam_anim.state.setAnimation(0, "animation1", false);
        SoundManager.Robot_Shoot_sound1.play();

        // 3) Place path endpoints (controller bones)
        if (this._boneStart && this._boneTarget) {
            this.setBoneToGlobal(this._boneStart, muzzleGlobal);
            this.setBoneToGlobal(this._boneTarget, targetGlobal);
        }

        // 4) Push transforms before animating the path value
        this.beam_anim.update(0); // evaluates constraints/poses without advancing time

        // 5) Drive along the path by tweening the path constraint's "position"
        if (this._beamConstraint) {
            // If your PathConstraint’s Position mode is Percent, 0→1 is ideal.
            // Your JSON shows values like 1→2.1962 in animation2, so we’ll normalize to 0→1:
            // Most reliable approach: just set 0 at start, 1 at end and let the runtime map it.
            this._beamConstraint.position = 0;

            gsap.to(this._beamConstraint, {
                position: 1,
                duration: 0.35,
                ease: "power2.out",
                onUpdate: () => {
                    this.beam_anim.update(0); // keep constraint evaluated each frame
                },
                onComplete: () => {
                    this.beam_anim.state.setAnimation(0, "animation2", false);
                    // Impact VFX and resolve
                    box.laser_burst!.visible = true;
                    box.laser_burst!.state.setAnimation(0, "animation", false);
                    box.laser_burst!.state.timeScale = 1;
                    setTimeout(() => {
                        box.shatterAnim!.visible = true;
                        SoundManager.Box_Shatter.play();

                        // Turn on WinBlock visibility at reveal time
                        // Reveal WinBlock layer now that the overlay is shattering
                        if (box.boxBg) box.boxBg.alpha = 1;

                        if (box.boxType === "power-up") {
                            box.boxBg?.state.setAnimation(0, "win_idle", true);
                        } else {
                            box.boxBg?.state.setAnimation(0, "static", true);
                        }


                        // Phase 1: keep block on idle (no shatter yet)
                        const tier = box.row_i! + 1;

                        // 1) Stop track 0 from re-applying the block attachment every frame
                        // (if you're looping staticTier on track 0, it will fight you)
                        box.shatterAnim!.state.clearTrack(0);

                        // 2) Force the correct tier to be displayed ONCE (optional but recommended)
                        box.shatterAnim!.skeleton.setAttachment("blocks", `block_t${tier}`);

                        // 3) Immediately hide the base block (this is what you want)
                        box.shatterAnim!.skeleton.setAttachment("blocks", null);

                        // 4) Play shatter overlay on track 0 (simpler) OR track 1 if you prefer
                        box.shatterAnim!.state.setAnimation(0, `revealTier${tier}`, false);



                        this.checkBonWin(box);
                    }, 300);
                }
            });
        }
    }
    animateRobo() {
        gsap.killTweensOf(this.roboAnim);

        const portrait = window.matchMedia("(orientation:portrait)").matches;

        if (portrait) {
            // Figma parrot rect (1080x1920 design space)
            const PARROT = { left: 638, top: 478, w: 416, h: 379 };
            const targetX = PARROT.left + PARROT.w / 2;   // 846
            const targetY = PARROT.top + PARROT.h / 2;    // 667.5

            // Slide in, then land exactly on the Figma position
            gsap.fromTo(
                this.roboAnim,
                { x: 1080 + 200, y: targetY },              // start off-screen right
                { x: targetX, y: targetY, duration: 0.8, ease: "power2.out" }
            );

            return;
        }

        // Landscape (your existing behavior)
        gsap.fromTo(
            this.roboAnim,
            { x: canvasProperties.width / 2, y: canvasProperties.height + this.roboAnim.height / 2, duration: 2, delay: 1 },
            { x: canvasProperties.width - this.roboAnim.width / 2, y: canvasProperties.height / 2 + 100 }
        );
    }

    createBlocksContainer() {
        this.blockAndFrameContainer = new PIXI.Container();
        this.blocksContainer = new PIXI.Container();

        this.bonusContainer.addChild(this.blockAndFrameContainer);

        const BlocksBg = new PIXI.Sprite(Assets.get("bonus_board"));
        BlocksBg.anchor.set(0.5);
        this.blockAndFrameContainer.addChild(BlocksBg);
        this.BlocksBg = BlocksBg;

        const frameMask = new PIXI.Graphics();
        frameMask.fill(0x000000).rect(0, 0, BlocksBg.width - 80, BlocksBg.height - 85);
        frameMask.endFill();
        frameMask.alpha = 0.5;
        frameMask.pivot.set(frameMask.x + frameMask.width / 2, frameMask.y + frameMask.height / 2);
        this.blockAndFrameContainer.addChild(frameMask);

        this.blocksContainer.mask = frameMask;
        this.blockAndFrameContainer.addChild(this.blocksContainer);

        // Clear any previous
        this.BlocksColContArr = [];
        this.Ind_BlocksArr = [];

        // We will compute stable step sizes from the first created block
        let singleBoxWidth = 0;
        let singleBoxHeight = 0;

        // IMPORTANT:
        // Use a stable baseline and step so Spine bounds/pivots don't affect stacking.
        const BASE_Y = BlocksBg.height / 3.75 + 2;
        const GAP_X = 20;   // smaller = tighter columns
        const GAP_Y = 10;   // smaller = tighter rows


        for (let i = 0; i < this.blocksOrder.length; i++) {
            const container: contType = new PIXI.Container();
            const blocksArr: PIXI.Container[] = [];

            const count = this.blocksOrder[i];

            for (let j = 0; j < count; j++) {
                const block = this.create_a_block(i);

                // IMPORTANT: measure spacing from the BASE WinBlock spine only
                // (overlay bounds must NOT affect spacing)
                const base = block.boxBg!;
                const bb = base.getLocalBounds();

                // bb is in spine-local; account for box_bg's internal scale (we kept it at 1)
                // box.scale is applied by PIXI container, but we want spacing pre-scale here.
                // So use raw bb sizes and apply BOX_SCALE via container scaling automatically.
                singleBoxWidth = bb.width - GAP_X;
                singleBoxHeight = bb.height - GAP_Y;


                // Center row around x=0
                block.x = (j - (count - 1) / 2) * singleBoxWidth;


                blocksArr.push(block);
                container.addChild(block);
            }

            // DO NOT pivot rows based on bounds (Spine bounds vary by animation/attachments)
            container.pivot.set(0, 0);

            // Start rows at the baseline; animateBoxes() will move them to backUp_Y
            container.y = BASE_Y;
            container.visible = false;

            const ROW_STEP = singleBoxHeight;
            container.backUp_Y = BASE_Y - i * ROW_STEP;

            this.Ind_BlocksArr.push(blocksArr);
            this.BlocksColContArr.push(container);
            this.blocksContainer.addChild(container);
        }

        // Keep same behavior as before
        this.blocksContainer.children.reverse();

        this.blockAndFrameContainer.x = canvasProperties.width / 2;
        this.blockAndFrameContainer.y = canvasProperties.height / 2;
    }

    animateBoxes(arr: contType[]) {
        const copyArr = arr.slice()
        copyArr[0].visible = true;
        const DestY = copyArr[0].backUp_Y;
        gsap.to(copyArr, { y: DestY, duration: 0.75, ease: "elastic.out(1,0.5)" });
        gsap.delayedCall(0.35, () => {
            copyArr.shift();
            if (copyArr.length > 0) this.animateBoxes(copyArr);
            else {
                this.Ind_BlocksArr.forEach((boxes, i) => {
                    boxes.forEach((box, i) => {
                        box.interactive = true;
                    });
                });
                this.updateBoxHighlights(0);
            }
        }, [copyArr])

    }

    create_a_block(index: number): contType {
        const box: contType = new PIXI.Container();

        // This is your overall block scale (keep this as your main knob)
        const BOX_SCALE = 0.8;
        box.scale.set(BOX_SCALE);

        // -----------------------
        // Win Block (base art)
        // -----------------------
        const box_bg = Spine.from({
            skeleton: "win_block_json",
            atlas: "win_block_atlas",
        });

        // IMPORTANT: keep internal spine scale at 1, and use BOX_SCALE above
        box_bg.scale.set(1);
        // Start hidden — we only show the WinBlock skeleton once revealed
        box_bg.state.setAnimation(0, "static", true);
        box_bg.state.timeScale = 1;

        box_bg.alpha = 0;          // ✅ hidden until reveal
        box_bg.visible = true;     // keep it in the display list, just invisible

        box.boxBg = box_bg;


        // Force origin alignment
        // (Spine containers default at (0,0) but we’ll hard-set)
        box_bg.position.set(0, 0);



        // -----------------------
        // Overlay (tier / shatter)
        // -----------------------
        const shatterAnim = Spine.from({
            skeleton: "bonus_blocks_anim_json",
            atlas: "bonus_blocks_anim_atlas",
        });

        shatterAnim.scale.set(1);
        shatterAnim.position.set(0, 0); // FORCE same origin as box_bg

        const tier = index + 1;
        shatterAnim.visible = true;
        shatterAnim.state.setAnimation(0, `staticTier${tier}`, true);
        shatterAnim.state.timeScale = 1;

        box.shatterAnim = shatterAnim;

        // -----------------------
        // Text (centered using WinBlock bounds)
        // -----------------------
        const box_Text = new PIXI.Text({ text: 1.0 });
        box_Text.style = {
            fontFamily: font_main,
            align: "center",
            fill: "white",
            fontSize: 30,
            fontWeight: "lighter",
            dropShadow: {
                alpha: 0.8,
                angle: 1,
                blur: 4,
                color: "0x111111",
                distance: 2,
            },
            stroke: {
                color: "black",
                width: 2,
                join: "round",
            },
            letterSpacing: 2,
        };
        box_Text.anchor.set(0.5);

        box.box_Text = box_Text;
        box.row_i = index;

        // Laser burst
        const laser_burst = Spine.from({
            skeleton: "laser_burst_anim_json",
            atlas: "laser_burst_anim_atlas",
        });
        laser_burst.state.setAnimation(0, "animation", false);
        laser_burst.state.timeScale = 0;
        laser_burst.visible = false;
        laser_burst.state.addListener({
            complete: () => {
                laser_burst.visible = true;
            },
        });
        box.laser_burst = laser_burst;

        // Add children in correct draw order
        // base -> text -> overlay -> burst
        box.addChild(box_bg, box_Text, shatterAnim, laser_burst);

        // -----------------------
        // KEY: Align everything using ONLY the base WinBlock bounds
        // -----------------------
        // Use WinBlock bounds as the “truth” for centering the box
        const bb = box_bg.getLocalBounds();

        // Center base around (0,0)
        box_bg.position.set(-(bb.x + bb.width / 2), -(bb.y + bb.height / 2));

        // Put overlay EXACTLY on top of the base
        // Put overlay on top of the base (manual tuning supported)
        shatterAnim.position.copyFrom(box_bg.position);

        // -----------------------
        // MANUAL STACK TUNING
        // -----------------------
        // Use these to stack the two skeletons by eye.
        // Positive Y = down. Negative Y = up.
        const WINBLOCK_OFFSET = new PIXI.Point(0, 50);   // moves win_block_json only
        const OVERLAY_OFFSET = new PIXI.Point(0, 0);   // moves bonus_blocks_anim_json only

        box_bg.position.x += WINBLOCK_OFFSET.x;
        box_bg.position.y += WINBLOCK_OFFSET.y;

        shatterAnim.position.x += OVERLAY_OFFSET.x;
        shatterAnim.position.y += OVERLAY_OFFSET.y;

        // ✅ Position text relative to the block center, with a single manual tuning knob
        box_Text.position.set(
            WINBLOCK_OFFSET.x + CURRENCY_TEXT_OFFSET.x,
            WINBLOCK_OFFSET.y + CURRENCY_TEXT_OFFSET.y
        );

        // Put burst on top of the same center
        laser_burst.position.set(0, 0);

        // -----------------------
        // Interaction
        // -----------------------
        box.animName = this.boxTypes[index];

        box.interactive = true;
        box.cursor = "pointer";

        box.on("pointerover", () => {
            box.filters = [brightnessFilter];
        });
        box.on("pointerout", () => {
            box.filters = [];
        });
        box.on("pointertap", () => {
            this.sendClickRequest(box.row_i!);
            this.Shoot_a_box(box);
        });

        return box;
    }


    Shoot_a_box(box: contType) {
        gsap.killTweensOf(brightnessFilter2);
        brightnessFilter2.brightness(1, false);
        box.parent.filters = [];

        // Robot anims first
        // this.roboAnim.state.applyMixingFrom(this.robo,this.roboAnim.skeleton,MixBlend)
        const idx = this.parrotVariantForRow(box.row_i!);
        this.roboAnim.state.setAnimation(0, `shoot_${this.fmt02(idx)}`, false);
        this.roboAnim.state.addAnimation(0, `idle_${this.fmt02(idx)}`, true);

        // Row becomes dull/non-interactive while firing
        this.Ind_BlocksArr[box.row_i!].forEach(el => {
            el.interactive = false;
            el.filters = [dullFilter];
        });
        box.filters = [];

        // Make beam visible and (optionally) play NON-path VFX on a different track
        this.beam_anim.visible = true;
        this.beam_anim.state.timeScale = 2.0; // keep normal; path is driven by code
        this.beam_anim.state.setEmptyAnimations();// default mix = 0
        // clear track 1 if used previously
        // Optional: play a small flash on Track 1 that DOES NOT key the path
        // this.beam_anim.state.setAnimation(1, "muzzle_flash_fx", false);

        // IMPORTANT: Do NOT move beam_anim.x/y after fireBeamTo sets bones
        // If you need a base placement, set it ONCE at init or BEFORE fireBeamTo and keep it fixed.
        // this.beam_anim.x = this.roboAnim.x - this.roboAnim.getLocalBounds().width / 2;
        // this.beam_anim.y = this.roboAnim.y;

        // Fire via path constraint tween (handles impact & shatter in its onComplete)
        setTimeout(() => {
            this.fireBeamTo(box);  // << this controls the beam travel and impact; no extra listeners needed
        }, 150);
    }

    sendClickRequest(row: number) {
        // let bonusData = [["power-up",1,4,3,5,4,3],["power-up",1,4,3,5],[30,"power-up",50],["power-up",100],[1000]];
        const rowData = generateBonusRow(row);//bonusData[row]//
        this.updateRow(rowData, row)
    }

    updateBoxHighlights(index: number) {
        this.BlocksColContArr.forEach((boxCont, i) => {
            if (i > index) {
                boxCont.filters = [dullFilter2];
                boxCont.interactiveChildren = false;
            }
            else if (i == index) {
                boxCont.filters = [brightnessFilter2];
                boxCont.interactiveChildren = true;

            }
            else boxCont.filters = []
        })
        // gsap.to(brightnessFilter2,{brightness:1,repeat:-1,yoyo:true}) 
    }

    private revealRow(rowIndex: number) {
        const row = this.Ind_BlocksArr[rowIndex];
        if (!row) return;

        row.forEach((box) => {
            // stop interaction on the row
            box.interactive = false;
            box.filters = [];
            (box.parent as any).filters = [];

            // hide tier overlay (so the WinBlock can be seen)
            if (box.shatterAnim) box.shatterAnim.visible = false;

            // show WinBlock layer for ALL boxes in this row
            if (box.boxBg) box.boxBg.alpha = 1;

            // drive animation based on what the server decided for this box
            if (box.boxType === "power-up") {
                box.boxBg?.state.setAnimation(0, "win_idle", true);
            } else {
                box.boxBg?.state.setAnimation(0, "static", true);
            }
        });
    }


    resetBonus() {
        this.BlocksBg.texture = Assets.get("bonus_board"); // optional, but safe

        this.bonusContainer.visible = true;
        this.BlocksColContArr.forEach((boxCont, i) => {
            boxCont.y = 0;
        })
        this.Ind_BlocksArr.forEach((boxes, j) => {
            boxes.forEach((box, i) => {
                box.shatterAnim!.visible = true;
                box.shatterAnim!.state.setAnimation(0, `staticTier${j + 1}`, true);

                // ✅ Reset WinBlock base to clean state every time
                box.boxBg?.state.setAnimation(0, "static", true);
                box.boxBg!.alpha = 0;          // ✅ hide until reveal
                box.boxType = undefined;
                box.win = undefined;

                // Reset text
                if (box.box_Text) box.box_Text.text = "";

                box.filters = [];
                box.parent.filters = [];
            });

        });
    }
    updateRow(rowData: (string | number)[], row: number) {
        this.Ind_BlocksArr[row].forEach((box, i) => {
            if (String(rowData[i]) == "power-up") {
                box.box_Text!.text = "";
                // Keep base art static until the reveal moment (prevents "win_idle behind block")
                box.boxBg!.state.setAnimation(0, "static", true);
                box.boxType = "power-up";
            }
            else {
                box.box_Text!.text = currencySymbol + (Number(rowData[i]).toFixed(2));
                box.box_Text!.scale.set(0.8);
                if (box.box_Text!.width > 100) {
                    box.box_Text!.scale.set(100 / box.box_Text!.width);
                }

                box.boxType = "win";
                box.win = Number(rowData[i]);

                box.boxBg!.alpha = 1; // ✅ reveal the WinBlock layer now
                box.boxBg!.state.setAnimation(0, "static", true);
            }
            box.interactive = false;
        });
    }
    checkBonWin(box: contType) {
        if (box.row_i! >= this.BlocksColContArr.length - 1) {
            SoundManager.Bonus_Celebration.play();
            setTimeout(() => {
                // ✅ Reveal the entire final row now
                this.revealRow(box.row_i!);

                this.bonusWin = box.win!;
                setTimeout(() => {
                    this.showBonusWin();
                }, 500);
            }, 500);

        } else if ((box.row_i! >= this.BlocksColContArr.length - 2) && box.boxType == "power-up") {

            {
                const winIdx = this.parrotVariantForRow(box.row_i!);
                const nextIdleIdx = this.parrotVariantForRow(box.row_i! + 1);
                this.roboAnim.state.setAnimation(0, `win_${this.fmt02(winIdx)}`, false);
                this.roboAnim.state.addAnimation(0, `idle_${this.fmt02(nextIdleIdx)}`, true);
            }

            this.BlocksColContArr[this.BlocksColContArr.length - 1].children[0].interactive = false;

            setTimeout(() => {
                this.Ind_BlocksArr[box.row_i!].forEach((element, i) => {
                    element.shatterAnim!.visible = false;
                });
                this.sendClickRequest(box.row_i! + 1)
                this.Shoot_a_box(this.BlocksColContArr[this.BlocksColContArr.length - 1].children[0])
            }, 500);
        }
        else if (box.boxType == "power-up") {
            SoundManager.Robot_Powerup.play();
            {
                const winIdx = this.parrotVariantForRow(box.row_i!);
                const nextIdleIdx = this.parrotVariantForRow(box.row_i! + 1);
                this.roboAnim.state.setAnimation(0, `win_${this.fmt02(winIdx)}`, false);
                this.roboAnim.state.addAnimation(0, `idle_${this.fmt02(nextIdleIdx)}`, true);
            }


            setTimeout(() => {
                this.highlightBoxes();
                this.updateBoxHighlights(box.row_i! + 1);
                this.Ind_BlocksArr[box.row_i!].forEach((element, i) => {
                    element.shatterAnim!.visible = false;
                });
            }, 500);
        } else {
            SoundManager.Bonus_Win_Robot_reveals_win.play();

            setTimeout(() => {
                // ✅ Reveal the entire current row (including starting win_idle for power-ups)
                this.revealRow(box.row_i!);

                this.bonusWin = box.win!;

                setTimeout(() => {
                    this.showBonusWin();
                }, 500);
            }, 500);
        }
    }

    startBonus() {
        this.visible = true;
        this.bonusContainer.visible = false;
        this.blockAndFrameContainer.visible = false;

        this.transition.visible = true;

        // ✅ Always layout transition right before playing it
        const screenW = this.app?.renderer?.screen?.width ?? canvasProperties.width;
        const screenH = this.app?.renderer?.screen?.height ?? canvasProperties.height;
        const isPortraitNow = screenH > screenW;
        this.layoutBonusTransition(isPortraitNow);

        this.transition.state.timeScale = 1;
        this.transition.state.setAnimation(0, "animation", false);
    }

    showBonus() {
        visibleElements = [];
        this.app.stage.children.forEach(obj => {
            if (obj.visible) {
                visibleElements.push(obj)
            }
            obj.visible = false;
        })
        this.visible = true
        LogoAnim.visible = true;

        this.roboAnim.state.setAnimation(0, "idle_01", true);
        this.bonusBg.texture = Assets.get("bonus_bg");
        SoundManager.Bonus_Enter_Transition.play();
        SoundManager.Bgmusic_Bonus.play()
        SoundManager.Bgmusic_Main.stop();

        // this.transition.state.setAnimation(0, "animation2", false);
        this.blockAndFrameContainer.visible = true;
        this.resetBonus()
        this.animateBoxes(this.BlocksColContArr)
        this.roboAnim.visible = true;
        this.animateRobo();
        this.highlightBoxes();
    }
    highlightBoxes() {
        const proxy = { b: 1 }; // 1 = normal
        gsap.to(proxy, {
            b: 1.6,
            duration: 0.6,
            ease: "sine.inOut",
            yoyo: true,
            repeat: -1,
            onUpdate() {
                brightnessFilter2.brightness(proxy.b, false); // false = don’t multiply
            }
        });
    }
    showBonusWin() {
        LogoAnim.allsuper = false;
        LogoAnim.winPopup!.showWinpopup(this.bonusWin, "", () => {
            setTimeout(() => {
                this.transition.visible = true;
                SoundManager.Bonus_Exit_Transition.play();
                // this.transition.state.setAnimation(0, "animation2", false);
                SoundManager.Bgmusic_Bonus.stop()
                SoundManager.Bgmusic_Main.play();
                setTimeout(() => {
                    this.endBonus()
                }, 500);
            }, 150);
        })
    }


    endBonus() {
        this.bonusBg.texture = Assets.get("bg_game_wide");
        LogoAnim.insideBonus = false;

        // Restore all hidden main-game objects
        visibleElements.forEach((element) => {
            element.visible = true;
        });

        // IMPORTANT: clear cached base scales so next layout is clean
        clearFigmaBaseScaleCache(this.app.stage);

        // ✅ Determine orientation from the renderer (source of truth),
        // NOT matchMedia (can be stale/wrong)
        const screenW = this.app.renderer.screen.width;
        const screenH = this.app.renderer.screen.height;
        const isPortraitNow = screenH > screenW;

        // ✅ Re-apply main-game layout
        if (isPortraitNow) {
            PortraitAdjustFun();
        } else {
            landScapeAdjustFun();
        }

        // ✅ Force your resize listeners to also run (covers any stale layout state)
        window.dispatchEvent(new Event("resize"));

        this.bonusContainer.visible = false;
        brightnessFilter2.brightness(1, false);
        gsap.killTweensOf(brightnessFilter2);
    }


    // Portrait Figma targets (design space 1080x1920 in your resizer)
    private readonly FIGMA_P = {
        BOARD: { left: 28, top: 741, w: 1026, h: 597 },

        // Figma: width 1681, centered with -26.5px x offset.
        // Convert to explicit left/top in 1080-wide space:
        // left = (1080 - 1681)/2 - 26.5 = -327
        INFO_TEXT: { left: -327, top: 1382, w: 1681, h: 113 },

        // Parrot tuning (relative to board center). Positive Y moves DOWN.
        PARROT_OFFSET: { x: 0, y: 0 },
    } as const;

    PortraitAdjustFun() {
        if (!this) return;

        // Kill tweens that might fight positioning
        gsap.killTweensOf(this.roboAnim);

        // Portrait Figma targets (design space 1080x1920)
        const BOARD = { left: -91, top: 669, w: 1265, h: 735 };
        const PARROT = { left: 638, top: 478, w: 416, h: 379 };

        // Figma: width 1681, centered with -26.5px x offset.
        // left = (1080 - 1681)/2 - 26.5 = -327
        const INFO = { left: -327, top: 1382, w: 1681, h: 113 };

        // ---- helper: scale+center a container into a target rect (contain) ----
        const fitContainerToRect = (
            container: PIXI.Container,
            rect: { left: number; top: number; w: number; h: number }
        ) => {
            const b = container.getLocalBounds();
            if (b.width < 1 || b.height < 1) return false;

            const sx = rect.w / b.width;
            const sy = rect.h / b.height;
            const s = Math.min(sx, sy);

            container.scale.set(s);
            container.pivot.set(b.x + b.width / 2, b.y + b.height / 2);
            container.position.set(rect.left + rect.w / 2, rect.top + rect.h / 2);
            return true;
        };

        // ---- 1) Board (container with background + blocks) ----
        // Prefer using BlocksBg as a stable bounds reference by forcing bounds update.
        // (If assets haven't drawn yet, getLocalBounds() can be 0 on first frame.)
        const okBoard = fitContainerToRect(this.blockAndFrameContainer, BOARD);

        // ---- 2) Parrot (Spine) ----
        // Place and scale the parrot to its own Figma rect.
        const okParrot = fitContainerToRect(this.roboAnim, PARROT);

        // If bounds weren't ready this frame, retry next frame
        if (!okBoard || !okParrot) {
            requestAnimationFrame(() => this.PortraitAdjustFun());
        }

        // ---- 3) Info text ----
        // Match Figma typography and position
        this.bonusInfotext.style.fontFamily = "RoadRage";
        this.bonusInfotext.style.fontWeight = "400";
        this.bonusInfotext.style.fontSize = 55;
        this.bonusInfotext.style.lineHeight = 61;
        this.bonusInfotext.style.align = "center";
        this.bonusInfotext.style.wordWrap = true;
        this.bonusInfotext.style.wordWrapWidth = INFO.w;

        // Center anchor and place to Figma rect center
        this.bonusInfotext.anchor?.set?.(0.5);
        this.bonusInfotext.position.set(INFO.left + INFO.w / 2, INFO.top + INFO.h / 2);

        // Keep your existing logo behavior
        if (LogoAnim.insideBonus) {
            LogoAnim.scale.set(0.15);
        }

        this.layoutBonusTransition(true);

    }



    landScapeAdjustFun() {
        if (this) {
            this.blockAndFrameContainer.scale?.set(1)
            this.blockAndFrameContainer.x = canvasProperties.width / 2;
            this.blockAndFrameContainer.y = canvasProperties.height / 2;

            this.bonusInfotext.style.fontSize = 60;
            this.bonusInfotext.style.wordWrap = false;
            this.bonusInfotext.position.set(canvasProperties.width / 2, canvasProperties.height / 2 + 360);
            // x:canvasProperties.width-this.roboAnim.width/2,y:canvasProperties.height/2+100
            gsap.killTweensOf(this.roboAnim)
            this.roboAnim.x = canvasProperties.width - this.roboAnim.width / 2;
            this.roboAnim.y = canvasProperties.height / 2 + 100;
            this.roboAnim.scale.set(1)
            if (LogoAnim.insideBonus) {
                LogoAnim.scale.set(0.2);
            }
        }
        this.layoutBonusTransition(false);
    }
}